// 2026-09-30 addition — admin creates staff accounts by email + full name;
// backend auto-generates the temp password and emails it (see
// /api/admin/users in main.py). Uses its own "admin_token", not the
// regular staff "token".
//
// P2 — lab distinction added: every account now gets a single `lab`
// assignment at creation time (not a retrofit — built into the same
// dialog/form from the start, same discipline as the proovereport admin
// UI fix). Lab options pulled LIVE from /api/config/labs (the real,
// pre-existing lab_configs collection) rather than hardcoded — extensible
// the moment a second lab gets added there.
//
// Visual pass — this page was a bare MUI default layout (plain <Paper>,
// no page header bar, no avatar/chips) while the rest of this app (and the
// proovereport admin dashboard we just rebuilt) uses a consistent header-
// bar + card + chip pattern. Brought this in line: page header bar, a
// bordered card table with initials avatars and status/lab chips, and a
// sectioned create-account dialog instead of an inline form.
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { extractErrorDetail } from "./validators";
import {
  Box, Typography, TextField, Button, Alert, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Select, MenuItem,
  Container, Tooltip,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import axios from "axios";
import LabConfigPanel from "./Labconfigpanel"; // 2026-10-05 — moved here from NavUserMenu.js, see button comment below
import AdminPageShell from "./AdminPageShell";
import { formatEST } from "../dateUtils";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const BRAND_NAVY = "#00357a";

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("admin_token")}` };
}

function initials(name) {
  return (name || "?").trim().split(/\s+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [lab, setLab] = useState(""); // P2
  const [labs, setLabs] = useState([]); // P2 — live from /api/config/labs
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [resettingEmail, setResettingEmail] = useState(null); // 2026-10-04 — per-row Reset Password loading state
  const [configOpen, setConfigOpen] = useState(false); // 2026-10-05 — Configure Labs dialog, moved here from NavUserMenu.js
  const [search, setSearch] = useState(""); // 2026-10-04 — the staff table had no search/filter at all; matches name/email/lab, client-side (list is small, same as every other admin table in this app)

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/api/admin/users`, { headers: authHeaders() });
      setUsers(res.data.users || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin-login");
      }
    }
  };

  const fetchLabs = async () => {
    try {
      // New admin-scoped endpoint (main.py, /api/admin/labs) — reads the
      // same lab_configs collection the report generation screens already
      // use (one source of truth, per the plan's "second biggest
      // finding"), but gated by the ADMIN token this page actually holds,
      // not the regular-staff token /api/config/labs requires (an admin
      // session has no reason to also carry a staff login).
      const res = await axios.get(`${API}/api/admin/labs`, { headers: authHeaders() });
      const labList = res.data || [];
      setLabs(labList);
      if (labList.length > 0 && !lab) setLab(labList[0].lab_id);
    } catch (err) {
      console.error("Failed to load labs", err);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) {
      navigate("/admin-login");
      return;
    }
    fetchUsers();
    fetchLabs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!lab) {
      setError("Please select a lab.");
      return;
    }
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await axios.post(
        `${API}/api/admin/users`,
        { email: email.trim(), full_name: fullName.trim(), lab }, // P2
        { headers: authHeaders() },
      );
      setSuccess(res.data.message);
      setEmail("");
      setFullName("");
      fetchUsers();
    } catch (err) {
      setError(extractErrorDetail(err, "Failed to create user."));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (targetEmail) => {
    setResettingEmail(targetEmail);
    setError(""); setSuccess("");
    try {
      const res = await axios.post(
        `${API}/api/admin/users/reset-password`,
        { email: targetEmail },
        { headers: authHeaders() },
      );
      setSuccess(res.data.message);
      fetchUsers();
    } catch (err) {
      setError(extractErrorDetail(err, "Failed to reset password."));
    } finally {
      setResettingEmail(null);
    }
  };

  return (
    <AdminPageShell
      isDark={isDark}
      title="Admin — Staff Accounts"
      subtitle="Create staff accounts and assign each one to a lab"
      actions={
        <>
          {/* 2026-10-04 — the consolidated RCM/payment dashboard, see
              ADMIN_RCM_CONSOLIDATION_PLAN.md. Separate page/route from
              this one and from the audit log — a new tab, not a merge. */}
          <Button variant="contained" size="small" onClick={() => navigate("/admin/rcm")}
            sx={{ bgcolor: BRAND_NAVY, textTransform: "none", fontWeight: 600, "&:hover": { bgcolor: "#002a61" } }}>
            RCM Dashboard
          </Button>
          {/* 2026-10-04 — real counterpart to labariumaiportal's own
              /admin "User Dashboard" (stat tiles + unified patient/
              clinician roster + name/email/phone search) — confirmed
              gap, distinct from RCM Dashboard above (that one is split
              by role and payment-focused). */}
          <Button variant="outlined" size="small" onClick={() => navigate("/admin/users")}
            sx={{ borderColor: BRAND_NAVY, color: BRAND_NAVY, textTransform: "none", fontWeight: 600 }}>
            User Dashboard
          </Button>
          <Button variant="outlined" size="small" onClick={() => navigate("/admin/audit-log")}
            sx={{ borderColor: BRAND_NAVY, color: BRAND_NAVY, textTransform: "none", fontWeight: 600 }}>
            View Audit Log
          </Button>
          {/* Order a Test, Part 12 — provider_registry (same physician,
              multiple practices) + clinician_patient_records linking
              status, both admin-visibility-only. */}
          <Button variant="outlined" size="small" onClick={() => navigate("/admin/identity-linking")}
            sx={{ borderColor: BRAND_NAVY, color: BRAND_NAVY, textTransform: "none", fontWeight: 600 }}>
            Identity Linking
          </Button>
          {/* Lab Orders — admin, cross-lab order-browsing: the full
              Lab → Clinician → Patient → Order tree, click-through to a
              full digital-requisition view. */}
          <Button variant="outlined" size="small" onClick={() => navigate("/admin/lab-orders")}
            sx={{ borderColor: BRAND_NAVY, color: BRAND_NAVY, textTransform: "none", fontWeight: 600 }}>
            Lab Orders
          </Button>
          {/* 2026-10-21 — client-facing verification dashboard: browse the
              REAL self-service GCS tree a patient/clinician would actually
              see, to confirm a just-uploaded report landed in the right
              identity/clinician folder. See docs/reference/
              LABARIUM_ISSUE_CHECKLIST_20261021.md's PLANNED section. */}
          <Button variant="outlined" size="small" onClick={() => navigate("/admin/client-facing")}
            sx={{ borderColor: BRAND_NAVY, color: BRAND_NAVY, textTransform: "none", fontWeight: 600 }}>
            Client-Facing Verification
          </Button>
          {/* 2026-10-05 — moved here from the regular staff NavUserMenu,
              per explicit instruction: lab/report-type configuration is
              an admin capability, not something every staff account
              should self-serve. Its own axios calls never sent an auth
              header at all before this move (the real cause of "failed
              to load labs"), now fixed to use this dashboard's own
              admin_token. */}
          <Button variant="outlined" size="small" onClick={() => setConfigOpen(true)}
            sx={{ borderColor: BRAND_NAVY, color: BRAND_NAVY, textTransform: "none", fontWeight: 600 }}>
            Configure Labs
          </Button>
          {/* 2026-10-04 — cross-link to the other app's admin, per the
              confirmed SSO-depth decision: same ADMIN_EMAIL/ADMIN_PASSWORD
              value in both apps' env vars, but separate sessions/JWTs —
              this button just saves having to remember/type the second
              URL, not a real single-sign-on. Only renders if the env var
              is actually set (no broken link to nowhere in dev/local). */}
          {process.env.REACT_APP_PORTAL_ADMIN_URL && (
            <Button variant="text" size="small"
              href={process.env.REACT_APP_PORTAL_ADMIN_URL}
              target="_blank" rel="noopener noreferrer"
              sx={{ color: "#888", textTransform: "none", fontWeight: 600, fontSize: "0.78rem" }}>
              Switch to Portal Admin →
            </Button>
          )}
        </>
      }
    >
      <Container maxWidth="md">
        <Box sx={{
          backgroundColor: isDark ? "#111827" : "#FFF",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
          borderRadius: 2, p: 3, mb: 3,
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 3, height: 18, backgroundColor: BRAND_NAVY, borderRadius: 1 }} />
            <Typography sx={{ fontWeight: 600, fontSize: "0.95rem", color: isDark ? "#f1f5f9" : "#111" }}>Create a new account</Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          <Box component="form" onSubmit={handleCreate} noValidate sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "flex-end" }}>
            <TextField label="Full name" size="small" required value={fullName}
              onChange={(e) => setFullName(e.target.value)} sx={{ minWidth: 220 }} />
            <TextField label="Email" type="email" size="small" required value={email}
              onChange={(e) => setEmail(e.target.value)} sx={{ minWidth: 260 }} />
            <Box sx={{ minWidth: 200 }}>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.07em", textTransform: "uppercase", color: isDark ? "#94a3b8" : "#555", mb: 0.5 }}>
                Lab
              </Typography>
              <Select size="small" fullWidth displayEmpty value={lab} onChange={(e) => setLab(e.target.value)} required>
                <MenuItem value="" disabled>Select a lab</MenuItem>
                {labs.map((l) => (
                  <MenuItem key={l.lab_id} value={l.lab_id}>{l.display_name || l.lab_id}</MenuItem>
                ))}
              </Select>
            </Box>
            <Button type="submit" variant="contained" disabled={loading} startIcon={loading ? null : <Add fontSize="small" />} sx={{ height: 40 }}>
              {loading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Create & Email Credentials"}
            </Button>
          </Box>
        </Box>

        {/* 2026-10-04 fix — real bug, reported directly ("I didn't even see
            the message when I did it"): the only error/success Alert lives
            inside the "Create a new account" card at the TOP of the page,
            but Reset Password is a button down in this table, often well
            below the fold — the confirmation rendered off-screen above
            where the admin was actually looking. Duplicated right here,
            next to the actions that are actually taken at this scroll
            position. */}
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}

        <TextField size="small" placeholder="Search by name, email, or lab…" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1.5, width: 320, bgcolor: isDark ? "#111827" : "#FFF" }} />

        <Box sx={{
          backgroundColor: isDark ? "#111827" : "#FFF",
          border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #E8E8E8",
          borderRadius: 2, overflow: "hidden",
        }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: isDark ? "rgba(255,255,255,0.03)" : "#F7F7F8" }}>
                {["User", "Email", "Lab", "Status", "Created", "Actions"].map((h) => (
                  <TableCell key={h} sx={{
                    fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase",
                    color: isDark ? "#94a3b8" : "#555",
                    borderBottom: isDark ? "2px solid rgba(255,255,255,0.1)" : "2px solid #E4E4E7",
                  }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const q = search.trim().toLowerCase();
                const filtered = q
                  ? users.filter((u) =>
                      (u.full_name || "").toLowerCase().includes(q) ||
                      (u.email || "").toLowerCase().includes(q) ||
                      (u.lab || "").toLowerCase().includes(q))
                  : users;
                if (users.length === 0) {
                  return <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "#AAA", fontSize: "0.82rem" }}>No accounts created yet.</TableCell></TableRow>;
                }
                if (filtered.length === 0) {
                  return <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "#AAA", fontSize: "0.82rem" }}>No accounts match "{search}".</TableCell></TableRow>;
                }
                return filtered.map((u) => (
                <TableRow key={u._id} sx={{ "&:hover": { backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#FAFAFA" } }}>
                  <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: "50%", backgroundColor: BRAND_NAVY, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <Typography sx={{ color: "#FFF", fontSize: "0.62rem", fontWeight: 700 }}>{initials(u.full_name)}</Typography>
                      </Box>
                      <Typography sx={{ fontSize: "0.85rem", color: isDark ? "#e5e7eb" : "inherit" }}>{u.full_name}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontFamily: "monospace", fontSize: "0.8rem", color: isDark ? "#e5e7eb" : "inherit" }}>{u.email}</TableCell>
                  <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                    <Chip size="small" label={u.lab || "—"} sx={{ backgroundColor: isDark ? "rgba(0,53,122,0.25)" : "#F0F4FF", color: isDark ? "#9db9e8" : "#1A3A7D", fontSize: "0.68rem", height: 20, borderRadius: 1 }} />
                  </TableCell>
                  <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                    <Chip size="small" label={u.force_password_change ? "Pending first login" : "Active"}
                      color={u.force_password_change ? "warning" : "success"} variant="outlined" />
                  </TableCell>
                  <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5", fontSize: "0.8rem", color: isDark ? "#94a3b8" : "#666" }}>
                    {formatEST(u.created_at, { dateOnly: true })}
                  </TableCell>
                  <TableCell sx={{ borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid #F4F4F5" }}>
                    <Tooltip title="Generate a new temporary password and email it to this user">
                      <Button size="small" variant="text" disabled={resettingEmail === u.email}
                        onClick={() => handleResetPassword(u.email)}
                        sx={{ textTransform: "none", fontSize: "0.75rem", color: isDark ? "#c9a96e" : BRAND_NAVY, fontWeight: 600 }}>
                        {resettingEmail === u.email ? <CircularProgress size={14} /> : "Reset Password"}
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </Box>
      </Container>

      <LabConfigPanel open={configOpen} onClose={() => setConfigOpen(false)} />
    </AdminPageShell>
  );
}
